import { supabase } from './supabase';

export const TERMO_TESTEMUNHA_1_KEY = 'termo_testemunha_1_user_id';
export const TERMO_TESTEMUNHA_2_KEY = 'termo_testemunha_2_user_id';
export const TERMO_PROCURADOR_KEY = 'termo_procurador_user_id';

export interface TestemunhaTermo {
    nome: string;
    cpf: string;
    email: string; // e-mail corporativo — usado como signatário no Autentique
}

export interface ProcuradorTermo {
    nome: string;
    email: string; // e-mail corporativo — recebe o e-mail do Autentique
    cpf?: string;
}

export interface SignatariosTermo {
    testemunha1: TestemunhaTermo;
    testemunha2: TestemunhaTermo;
    procurador: ProcuradorTermo;
}

const CONFIG_HINT = 'Configure em Configurações → Assinaturas do Termo.';

/** Signatário genérico enviado aos webhooks n8n dos termos (advertência, mora, aditivo, distrato, adesão). */
export interface SignatarioTermo {
    nome: string;
    cpf: string;
    email: string;
}

export interface SignatariosEscolhidos {
    procurador: SignatarioTermo | null;
    testemunha1: SignatarioTermo | null;
    testemunha2: SignatarioTermo | null;
}

/**
 * Versão tolerante de fetchSignatariosTermo: retorna o que estiver configurado
 * (sem lançar) para servir de DEFAULT nos seletores dos modais de documentos.
 * Documentos que não usam testemunhas funcionam só com o procurador.
 */
export async function fetchSignatariosDefault(): Promise<SignatariosEscolhidos> {
    const vazio: SignatariosEscolhidos = { procurador: null, testemunha1: null, testemunha2: null };
    try {
        const { data: settings } = await supabase
            .from('system_settings')
            .select('key, value')
            .in('key', [TERMO_TESTEMUNHA_1_KEY, TERMO_TESTEMUNHA_2_KEY, TERMO_PROCURADOR_KEY]);

        const map: Record<string, string> = {};
        (settings || []).forEach((s: { key: string; value: string }) => { map[s.key] = s.value; });

        const ids = [map[TERMO_PROCURADOR_KEY], map[TERMO_TESTEMUNHA_1_KEY], map[TERMO_TESTEMUNHA_2_KEY]].filter(Boolean);
        if (ids.length === 0) return vazio;

        const { data: profiles } = await supabase
            .from('profiles')
            .select('id, full_name, cpf, email_corporativo')
            .in('id', ids);

        const byId: Record<string, any> = {};
        (profiles || []).forEach(p => { byId[p.id] = p; });

        const toSignatario = (id?: string): SignatarioTermo | null => {
            const p = id ? byId[id] : null;
            if (!p || !p.email_corporativo?.trim()) return null;
            return { nome: p.full_name || '', cpf: p.cpf || '', email: p.email_corporativo };
        };

        return {
            procurador: toSignatario(map[TERMO_PROCURADOR_KEY]),
            testemunha1: toSignatario(map[TERMO_TESTEMUNHA_1_KEY]),
            testemunha2: toSignatario(map[TERMO_TESTEMUNHA_2_KEY])
        };
    } catch (err) {
        console.error('Erro ao buscar signatários padrão:', err);
        return vazio;
    }
}

/**
 * Busca e valida as testemunhas e o procurador configurados para o termo de adesão.
 * Lança Error com mensagem amigável (para toast) se a configuração estiver incompleta.
 */
export async function fetchSignatariosTermo(): Promise<SignatariosTermo> {
    const { data: settings, error: settingsError } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', [TERMO_TESTEMUNHA_1_KEY, TERMO_TESTEMUNHA_2_KEY, TERMO_PROCURADOR_KEY]);

    if (settingsError) throw new Error('Erro ao buscar signatários do termo: ' + settingsError.message);

    const map: Record<string, string> = {};
    (settings || []).forEach((s: { key: string; value: string }) => { map[s.key] = s.value; });

    const t1Id = map[TERMO_TESTEMUNHA_1_KEY];
    const t2Id = map[TERMO_TESTEMUNHA_2_KEY];
    const procId = map[TERMO_PROCURADOR_KEY];

    if (!t1Id || !t2Id || !procId) {
        throw new Error(`Testemunhas e procurador do termo não configurados. ${CONFIG_HINT}`);
    }

    const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, cpf, email_corporativo, status')
        .in('id', [t1Id, t2Id, procId]);

    if (profilesError) throw new Error('Erro ao buscar perfis dos signatários: ' + profilesError.message);

    const byId: Record<string, any> = {};
    (profiles || []).forEach(p => { byId[p.id] = p; });

    const t1 = byId[t1Id];
    const t2 = byId[t2Id];
    const proc = byId[procId];

    if (!t1 || !t2 || !proc) {
        throw new Error(`Um dos signatários configurados não foi encontrado. ${CONFIG_HINT}`);
    }

    for (const t of [t1, t2]) {
        if (!t.cpf?.trim() || !t.email_corporativo?.trim()) {
            throw new Error(`A testemunha ${t.full_name} precisa de e-mail corporativo e CPF cadastrados (Configurações → Usuários e Permissões).`);
        }
    }

    if (!proc.email_corporativo?.trim()) {
        throw new Error(`O procurador ${proc.full_name} precisa de e-mail corporativo cadastrado (Configurações → Usuários e Permissões).`);
    }

    return {
        testemunha1: { nome: t1.full_name, cpf: t1.cpf, email: t1.email_corporativo },
        testemunha2: { nome: t2.full_name, cpf: t2.cpf, email: t2.email_corporativo },
        procurador: { nome: proc.full_name, email: proc.email_corporativo, cpf: proc.cpf || undefined }
    };
}
